import Head from "next/head";
import React, { ReactElement } from "react";
import styles from "@/containers/activities/holiday2022/index.module.scss";
import { UserOpenBrokerStatus } from "@/interfaces/pullNewType";
import { useTranslation } from "next-i18next";
import dayjs from "dayjs";
import { useGetInvitees } from "@/service/pullNew.service";
import EmptyContainer from "@/containers/common/EmptyContainer";
import { GetServerSideProps } from "next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import ActivityLayout from "@/layout/ActivityLayout";

import FooterBtn from "@/containers/activities/FooterBtn";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import hasLogin from "@/utils/hasLogin";
import directToClientLogin from "@/utils/directToClientLogin";
import { formatThousandSeparator } from "@/utils/formatThousandSeparator";
import useInviteShare from "@/hooks/useShare/useInviteShare";
import { ModulesMappings } from "@/interfaces/enums";

// 注销用户标识
const USER_IS_LOGGED_OUT = "USER_IS_LOGGED_OUT";

export default function Invitee() {
	const { t } = useTranslation("activities");
	const onShare = useInviteShare(ModulesMappings.holiday2022);

	const { invitees, loading } = useGetInvitees();

	const renderInvitee = (invitee: string) => {
		if (invitee === USER_IS_LOGGED_OUT) {
			return t("account-deleted");
		}
		return invitee;
	};

	const formatFontSize = (num: number) => {
		return String(num).length > 4 ? styles.maxSpan : "";
	};

	const renderStatusContent = (status: number) => {
		if (status === UserOpenBrokerStatus.DEPOSITED) {
			return <span className={styles.done}>{t("deposited-100USD")}</span>;
		}
		if (status === UserOpenBrokerStatus.OPEN_ACCOUNT) {
			return <span className={styles.pending}>{t("account-opened")}</span>;
		}
		if (status === UserOpenBrokerStatus.NO_COMMIT_MATERIAL) {
			return <span className={styles.unStart}>{t("registered")}</span>;
		}
		if (status === UserOpenBrokerStatus.COMMIT_MATERIAL) {
			return <span className={styles.unStart}>{t("registered")}</span>;
		}
		return null;
	};

	const renderInviteContent = () => {
		if (loading) {
			return (
				<>
					<ul>
						{new Array(4).fill("").map((item, key) => {
							return (
								<li key={key}>
									<div className={styles.left}>
										<Skeleton width={110} height={24} />
										<Skeleton width={80} height={18} />
									</div>

									<div className={styles.right}>
										<Skeleton width={80} height={24} />
									</div>
								</li>
							);
						})}
					</ul>
				</>
			);
		}

		if (invitees?.inviteList.length === 0) {
			return (
				<>
					<EmptyContainer title={t("no-friends-invited-yet")} subTitle={t("invite-your-friends-now")} />
				</>
			);
		}

		return (
			<>
				<ul>
					{invitees?.inviteList.map((item, key) => {
						return (
							<li key={key}>
								<div className={styles.left}>
									<p>{renderInvitee(item.invitee)}</p>
									<i>{dayjs(item.inviteTime).format("YYYY-MM-DD")}</i>
								</div>

								<div className={styles.right}>{renderStatusContent(item.inviteeStatus)}</div>
							</li>
						);
					})}
				</ul>
			</>
		);
	};

	return (
		<>
			<Head>
				<meta
					name="viewport"
					content="width=device-width, initial-scale=1.0, minimum-scale=1.0, maximum-scale=1.0, user-scalable=no"
				/>
				<title>{t("my-invitee")}</title>
			</Head>

			<section className={styles.section}>
				<div>
					<div className={styles.statuses}>
						<div>
							<p>{t("registered")}</p>
							<div className={styles.registed}>
								<span className={formatFontSize(invitees?.registeredCount || 0)}>
									{formatThousandSeparator(invitees?.registeredCount || 0)}
								</span>
							</div>
						</div>
						<div>
							<p>{t("account-opened")}</p>
							<div className={styles.accountOpened}>
								<span className={formatFontSize(invitees?.accountOpenedAccount || 0)}>
									{formatThousandSeparator(invitees?.accountOpenedAccount || 0)}
								</span>
							</div>
						</div>
						<div>
							<p>{t("deposited")}</p>
							<div className={styles.deposited}>
								<span className={formatFontSize(invitees?.depositCompletedCount || 0)}>
									{formatThousandSeparator(invitees?.depositCompletedCount || 0)}
								</span>
							</div>
						</div>
					</div>

					<div className={styles.inviteList}>{renderInviteContent()}</div>
				</div>

				<FooterBtn
					btnText={t("invite-friends")}
					onClick={() => {
						if (!hasLogin()) {
							directToClientLogin();
							return;
						}

						onShare();
					}}
				></FooterBtn>
			</section>
		</>
	);
}

export const getServerSideProps: GetServerSideProps = async (context) => {
	return {
		props: {
			...(await serverSideTranslations(context.locale as string, ["error", "common", "share", "activities"])),
		},
	};
};

Invitee.getLayout = function getLayout(page: ReactElement) {
	return <ActivityLayout>{page}</ActivityLayout>;
};
